import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck, Trash2, Sparkles, Info, AlertTriangle, Calendar, IdCard, Check, Loader2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const KIND_ICON = { info: Info, success: Check, warning: AlertTriangle, interview: Calendar, passport: IdCard, system: Sparkles };
const KIND_STYLE = {
    info: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    success: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    warning: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    interview: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
    passport: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    system: "bg-muted text-muted-foreground",
};

const NotificationsPage = () => {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/notifications", { params: { limit: 100 } });
            setItems(data.items || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { if (user) load(); }, [user, load]);

    const markAllRead = async () => {
        try { await api.post("/notifications/read", {}); load(); }
        catch (err) { toast.error(formatApiError(err)); }
    };

    const remove = async (id) => {
        try { await api.delete(`/notifications/${id}`); setItems((xs) => xs.filter((n) => n.id !== id)); }
        catch (err) { toast.error(formatApiError(err)); }
    };

    if (!user) {
        return (
            <div className="container-page py-24 text-center">
                <Bell size={26} className="mx-auto text-emerald-500" />
                <h1 className="mt-4 font-heading text-2xl font-medium">Please log in to see your notifications.</h1>
                <Link to="/login" className="btn-green mt-6 inline-flex">Log in</Link>
            </div>
        );
    }

    return (
        <div className="container-page py-12" data-testid="notifications-page">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
                <div>
                    <p className="overline">Activity</p>
                    <h1 className="mt-2 font-heading font-medium text-3xl lg:text-4xl tracking-tight text-foreground">Notifications</h1>
                </div>
                {items.some((n) => !n.read) && (
                    <button onClick={markAllRead} data-testid="notifs-mark-all-read" className="btn-outline inline-flex text-sm">
                        <CheckCheck size={14} className="mr-2" /> Mark all as read
                    </button>
                )}
            </div>
            {loading ? (
                <div className="py-16 flex items-center justify-center text-muted-foreground gap-2 text-sm"><Loader2 size={16} className="animate-spin" /> Loading…</div>
            ) : items.length === 0 ? (
                <div className="card-soft p-14 text-center">
                    <Bell size={26} className="mx-auto text-muted-foreground" />
                    <h3 className="mt-4 font-heading font-semibold text-xl text-foreground">You&apos;re all caught up.</h3>
                    <p className="mt-2 text-sm text-muted-foreground">When something new happens — an interview invite, a passport view, a match — it&apos;ll show up here.</p>
                </div>
            ) : (
                <div className="space-y-2" data-testid="notifs-list">
                    {items.map((n) => {
                        const Icon = KIND_ICON[n.kind] || Info;
                        return (
                            <div
                                key={n.id}
                                data-testid={`notif-row-${n.id}`}
                                className={`card-soft p-4 flex gap-4 items-start ${!n.read ? "border-emerald-500/40" : ""}`}
                            >
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${KIND_STYLE[n.kind] || KIND_STYLE.info}`}>
                                    <Icon size={16} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h3 className="font-heading font-semibold text-foreground">{n.title}</h3>
                                        {!n.read && <span className="rounded-full bg-brand-green text-white text-[10px] font-bold px-2 py-0.5">NEW</span>}
                                    </div>
                                    {n.body && <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{n.body}</p>}
                                    <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                                        <span>{new Date(n.created_at).toLocaleString()}</span>
                                        {n.href && <Link to={n.href} className="text-brand-green font-semibold hover:underline">Open →</Link>}
                                    </div>
                                </div>
                                <button
                                    onClick={() => remove(n.id)}
                                    data-testid={`notif-delete-${n.id}`}
                                    className="text-muted-foreground hover:text-destructive"
                                    aria-label="Delete"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default NotificationsPage;
